/**
 * Superhost API Client
 * 
 * ARCHITECTURE:
 * - CRM (comp.extranetsync.com) = Primary source for Leads & Properties
 * - Local Backend = Auth, Bookings, App features
 * 
 * This ensures data consistency with CRM while maintaining app functionality.
 */

import axios from 'axios';
import { CRM_CONFIG, LOCAL_CONFIG } from './apiConfig';

// ============================================================================
// CRM API CLIENT - Primary data source for Leads & Properties
// ============================================================================
const crmApi = axios.create({
  baseURL: CRM_CONFIG.BASE_URL,
  timeout: CRM_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${CRM_CONFIG.API_KEY}`,
    'X-API-Key': CRM_CONFIG.API_KEY,
  },
});

crmApi.interceptors.response.use(
  (response) => {
    console.log(`[CRM] Response ${response.status} from ${response.config?.url}`);
    return response.data;
  },
  (error) => {
    console.error(`[CRM] Error from ${error.config?.url}:`, error.message);
    return Promise.reject(error.response?.data || error);
  }
);

// ============================================================================
// LOCAL API CLIENT - Auth, Bookings, App features
// ============================================================================

const MAX_RETRIES = 2;
const RETRY_DELAY = 1500;

function getFriendlyError(error) {
  if (!error.response && !error.status) return 'Network error. Check your connection.';
  const status = error.response?.status || error.status;
  if (status === 401) return 'Session expired. Please login again.';
  if (status === 403) return 'Access denied.';
  if (status === 404) return 'Resource not found.';
  if (status === 408 || error.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (status === 422) {
    // iter90 — FastAPI / Pydantic v2 returns `detail` as an ARRAY of objects
    // ({type, loc, msg, input, url}). Returning the raw array used to crash
    // React when a consumer dropped it into JSX (e.g. toast description).
    // Always reduce it to a human-readable string.
    const d = error.response?.data?.detail;
    if (Array.isArray(d)) {
      return d
        .map((e) => (typeof e === 'string' ? e : (e?.msg || e?.message || JSON.stringify(e))))
        .join('; ') || 'Invalid data.';
    }
    if (typeof d === 'string') return d;
    return error.response?.data?.MESSAGE || 'Invalid data.';
  }
  if (status >= 500) return 'Server temporarily unavailable.';
  return 'Something went wrong. Please try again.';
}

const localApi = axios.create({
  baseURL: `${LOCAL_CONFIG.BASE_URL}/api`,
  timeout: LOCAL_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// iter147 — One-time boot log so QA can confirm the URL baked into the
// release APK (visible via chrome://inspect → WebView). If you ever see
// "Network Error" on Android again, the FIRST line to check is this log.
try {
  console.log('[Superhost] backend baseURL =', localApi.defaults.baseURL);
} catch { /* noop */ }

// Request interceptor to add auth token
localApi.interceptors.request.use((config) => {
  const { getToken } = require('./tokenService');
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Track retry count
  config._retryCount = config._retryCount || 0;
  return config;
});

// Response interceptor with auto-retry
localApi.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    // iter147 — Verbose Android-debug log for "Network Error" cases so the
    // full picture (URL + method + code + response object) surfaces in
    // chrome://inspect. Only prints when there is NO HTTP response (true
    // network failure), which is the only case that shows the generic
    // "Network Error" toast — every 4xx/5xx already carries a body.
    if (!error.response) {
      try {
        console.error('[Superhost NetErr]', {
          url: `${config?.baseURL || ''}${config?.url || ''}`,
          method: config?.method,
          code: error.code,
          message: error.message,
          hasRequest: Boolean(error.request),
          responseText: error.request?.responseText?.slice?.(0, 300),
        });
      } catch { /* noop */ }
    }

    // Don't retry auth endpoints or 4xx client errors (except 408 timeout)
    const isRetryable = !config?.url?.includes('/auth/') &&
      (!status || status >= 500 || status === 408 || error.code === 'ECONNABORTED' || !error.response);

    if (isRetryable && config && config._retryCount < MAX_RETRIES) {
      config._retryCount += 1;
      console.warn(`[LOCAL] Retry ${config._retryCount}/${MAX_RETRIES} for ${config.url}`);
      await new Promise(r => setTimeout(r, RETRY_DELAY * config._retryCount));
      return localApi(config);
    }

    // 401 → force logout (except auth endpoints)
    if (status === 401 && !config?.url?.includes('/auth/')) {
      const { clearAll } = require('./tokenService');
      clearAll();
      window.location.href = '/login';
    }

    // Attach friendly message — guard against non-object responses
    // (backend may return plain text "Internal Server Error" string on 500)
    const friendlyMessage = getFriendlyError(error);
    const rawData = error.response?.data;

    let enrichedError;
    if (rawData && typeof rawData === 'object') {
      // Normal JSON response — safe to mutate
      enrichedError = rawData;
    } else if (typeof rawData === 'string' && rawData.trim()) {
      // Plain string body (e.g. "Internal Server Error") — wrap in object
      enrichedError = { detail: rawData, message: rawData };
    } else {
      // No response body — fall back to error object (axios Error is mutable)
      enrichedError = { message: error.message || 'Request failed' };
    }
    enrichedError._friendlyMessage = friendlyMessage;
    enrichedError._status = status;

    return Promise.reject(enrichedError);
  }
);

// ============================================================================
// CRM APIs - Fetches LIVE data from CRM (comp.extranetsync.com)
// ============================================================================

export const crmAPI = {
  // Health check
  getHealth: () => crmApi.get('/health'),
  
  // Get all leads - LIVE from CRM
  getLeads: async () => {
    const response = await crmApi.get('/leads');
    return {
      STATUS: 1,
      MESSAGE: `Fetched ${response?.data?.length || 0} leads from CRM`,
      RESULT: response?.data || [],
      source: 'comp.extranetsync.com'
    };
  },
  
  // Get all properties - LIVE from CRM
  getProperties: async () => {
    const response = await crmApi.get('/properties');
    const properties = (response?.data || []).map(p => ({
      ...p,
      // Map CRM hotel_name to property_name for consistency
      property_name: p.propertyName || p.hotel_name || p.property_name || p.name || '',
      name: p.propertyName || p.hotel_name || p.property_name || p.name || '',
    }));
    return {
      STATUS: 1,
      MESSAGE: `Fetched ${properties.length} properties from CRM`,
      RESULT: properties,
      source: 'comp.extranetsync.com'
    };
  },
  
  // Get single property by ID
  getProperty: async (propertyId) => {
    const response = await crmApi.get(`/properties/${propertyId}`);
    const p = response?.data || response;
    return {
      ...p,
      property_name: p?.propertyName || p?.hotel_name || p?.property_name || p?.name || '',
    };
  },
  
  // Find property by phone
  findPropertyByPhone: async (phone) => {
    const response = await crmApi.get('/properties');
    const properties = response?.data || [];
    const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
    
    const property = properties.find(p => {
      const propPhone = (p.mobile || '').replace(/\D/g, '').slice(-10);
      return propPhone === normalizedPhone;
    });
    
    if (property) {
      return { 
        found: true, 
        data: {
          ...property,
          property_name: property.propertyName || property.hotel_name || property.property_name || property.name || '',
        }
      };
    }
    return { found: false, data: null };
  },
  
  // Find lead by phone
  findLeadByPhone: async (phone) => {
    const response = await crmApi.get('/leads');
    const leads = response?.data || [];
    const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
    
    const lead = leads.find(l => {
      const leadPhone = (l.phone || '').replace(/\D/g, '').slice(-10);
      return leadPhone === normalizedPhone;
    });
    
    if (lead) {
      return {
        found: true,
        data: lead,
        authorized: lead.lead_stage === 'CLOSED WON',
      };
    }
    return { found: false, data: null, authorized: false };
  },
};

// ============================================================================
// AUTH APIs - Uses Local Backend (CRM doesn't have auth)
// ============================================================================

export const authAPI = {
  // Legacy Phone OTP (2Factor SMS) — kept for backward compat only.
  requestOtp: (phoneNumber) =>
    localApi.post('/auth/request-otp', { phone_number: phoneNumber }),
  loginWithPhone: (phoneNumber) =>
    localApi.post('/auth/request-otp', { phone_number: phoneNumber }),
  verifyOtp: (phoneNumber, sessionId, otp) =>
    localApi.post('/auth/verify-otp', { phone_number: phoneNumber, session_id: sessionId, otp }),
  resendOtp: (phoneNumber, sessionId) =>
    localApi.post('/auth/resend-otp', { phone_number: phoneNumber, session_id: sessionId }),

  // iter121 — WhatsApp OTP Login (approved Meta AUTH template `superhost_login_otp`).
  // These replace the 2Factor SMS flow on the LoginScreen and Forgot Password
  // mobile channel. Email + Password login remains untouched.
  sendMobileOtp: (mobile) =>
    localApi.post('/auth/mobile/send-otp', { mobile }),
  verifyMobileOtp: (mobile, otp) =>
    localApi.post('/auth/mobile/verify-otp', { mobile, otp }),
  resendMobileOtp: (mobile) =>
    localApi.post('/auth/mobile/resend-otp', { mobile }),

  // Email + Password login
  loginWithEmail: (email, password) =>
    localApi.post('/auth/login/email', { email, password }),

  // Force-password-change handoff
  changePassword: (tempToken, newPassword) =>
    localApi.post('/auth/change-password', { temp_token: tempToken, new_password: newPassword }),

  register: (data) => localApi.post('/auth/register', data),
  login: (credentials) => localApi.post('/auth/login', credentials),
  refresh: () => localApi.post('/auth/refresh'),
  getProfile: () => localApi.get('/auth/profile'),
  updateProfile: (data) => localApi.put('/auth/profile', data),
  logout: () => localApi.post('/auth/logout'),
};

// ============================================================================
// INTERNAL ADMIN (BFR) APIs — read-only ops/monitoring console
// ============================================================================

export const adminAuthAPI = {
  login: (email, password) => localApi.post('/admin-auth/login', { email, password }),
  me: () => localApi.get('/admin-auth/me'),
};

export const adminAPI = {
  listProperties: (search = '', limit = 40, skip = 0) =>
    localApi.get('/admin/properties', { params: { search: search || undefined, limit, skip } }),
  getProperty: (propertyId) => localApi.get(`/admin/properties/${propertyId}`),
  getBookings: (propertyId, limit = 50, skip = 0) =>
    localApi.get(`/admin/properties/${propertyId}/bookings`, { params: { limit, skip } }),
  getCalendar: (propertyId, start, end) =>
    localApi.get(`/admin/properties/${propertyId}/calendar`, { params: { start, end } }),
  getOnboarding: (propertyId) => localApi.get(`/admin/properties/${propertyId}/onboarding`),

  // iter119 — BFR Ops Aggregate Calendar Dashboard
  calendarProperties: () => localApi.get('/admin/calendar/properties'),
  calendarCounts: (start, end, propertyId = null) =>
    localApi.get('/admin/calendar/counts', {
      params: { start, end, property_id: propertyId || undefined },
    }),
  calendarSummary: (propertyId = null) =>
    localApi.get('/admin/calendar/summary', {
      params: { property_id: propertyId || undefined },
    }),
  calendarBookingsForDate: (date, mode = 'checkin', propertyId = null, search = '') =>
    localApi.get('/admin/calendar/bookings', {
      params: {
        date,
        mode,
        property_id: propertyId || undefined,
        search: search || undefined,
      },
    }),
};

// ============================================================================
// BFR EMAIL APIs (iter158) — thin BFR wrapper around the CRM Gmail Integration.
// All routes gated by BFR admin JWT; backend proxies to
// POST/GET https://<crm>/api/external/gmail/{send,status} with X-Sync-Secret.
// ============================================================================

export const bfrEmailAPI = {
  gmailStatus: () => localApi.get('/admin/email/gmail-status'),
  // iter176 — Fetch templates configured in the booking-vault-admin panel.
  // Optional booking_id auto-filters by the booking's OTA on the server.
  listTemplates: (bookingId) =>
    localApi.get('/admin/email/templates', {
      params: bookingId ? { booking_id: bookingId } : undefined,
    }),
  resolveTemplate: ({ booking_id, subject, body, extras }) =>
    localApi.post('/admin/email/resolve', { booking_id, subject, body, extras }),
  // iter177 — Fetch the OTA partner/support email configured in the
  // admin panel so the modal can prefill the "To" field automatically.
  otaSupportEmail: ({ ota, booking_id } = {}) =>
    localApi.get('/admin/email/ota-support-email', {
      params: {
        ota: ota || undefined,
        booking_id: booking_id || undefined,
      },
    }),
  // iter182 — Global CC/BCC configured in booking-vault-admin. Read-only
  // in the composer; always included in the outgoing send payload.
  globalSettings: ({ refresh } = {}) =>
    localApi.get('/admin/email/global-settings', {
      params: refresh ? { refresh: 1 } : undefined,
    }),
  send: (payload) => localApi.post('/admin/email/send', payload),
  history: ({ booking_id, property_id, status, limit = 50, offset = 0 } = {}) =>
    localApi.get('/admin/email/history', {
      params: {
        booking_id: booking_id || undefined,
        property_id: property_id || undefined,
        status: status || undefined,
        limit,
        offset,
      },
    }),
  retry: (emailId) => localApi.post(`/admin/email/history/${emailId}/retry`),
};

// ============================================================================
// APP CONFIG (iter162) — Admin-controlled profile menu module toggles.
// Public read; admin (BFR) write. See routes/app_config_routes.py.
// ============================================================================

export const appConfigAPI = {
  profileModules: () => localApi.get('/app-config/profile-modules'),
  adminGetProfileModules: () => localApi.get('/admin/app-config/profile-modules'),
  adminUpdateProfileModules: (modules) =>
    localApi.put('/admin/app-config/profile-modules', modules),
};

// iter170 — Per-property settings (currently: GSTIN). Owner-managed.
export const propertySettingsAPI = {
  get: (propertyId) => localApi.get(`/property-settings/${propertyId}`),
  update: (propertyId, payload) => localApi.put(`/property-settings/${propertyId}`, payload),
};

// iter174 — Module config (bottom-nav toggles + per-property GST admin
// config). Optional propertyId scopes the payload so gst_enabled /
// gst_percentage can be published per property from the Admin Panel.
// iter180 — Optional `refresh=true` bypasses the backend cache so
// admin edits reflect within milliseconds (used on property switch
// and on BookingCreate open).
export const moduleConfigAPI = {
  get: (propertyId, opts = {}) => {
    const params = new URLSearchParams();
    if (propertyId) params.set('property_id', propertyId);
    if (opts.refresh) params.set('refresh', '1');
    const qs = params.toString();
    return localApi.get(`/config/modules${qs ? `?${qs}` : ''}`);
  },
};

// ============================================================================
// BOOKINGS APIs - Uses Local Backend (CRM doesn't have bookings)
// ============================================================================

export const bookingsAPI = {
  // Get all bookings for a property
  getAll: (propertyId = null, startDate = null, endDate = null, propertyName = null, grbId = null) =>
    localApi.get('/bookings/all', { 
      params: { 
        property_id: propertyId, 
        property_name: propertyName,
        grb_id: grbId || undefined,
        start_date: startDate, 
        end_date: endDate 
      } 
    }),
  
  // Get bookings by date
  getByDate: (date, propertyId = null) =>
    localApi.get('/bookings/by-date', {
      params: { date, property_id: propertyId }
    }),
  
  // Get booking by ID
  getById: (bookingId) => localApi.get(`/bookings/${bookingId}`),
  getBooking: (bookingId) => localApi.get(`/bookings/${bookingId}`),
  
  // Create new booking — trailing slash is REQUIRED:
  // FastAPI redirects POST /bookings → /bookings/ as 307, but the redirect Location
  // header uses http:// (proxy quirk) which the browser blocks under https → "Network Error".
  create: (bookingData) => localApi.post('/bookings/', bookingData),
  createBooking: (bookingData) => localApi.post('/bookings/', bookingData),
  
  // Update booking
  update: (bookingId, data) => localApi.put(`/bookings/${bookingId}`, data),
  updateBooking: (bookingId, data) => localApi.put(`/bookings/${bookingId}`, data),
  
  // Check-in
  checkIn: (bookingId, idProof = null) => 
    localApi.post(`/bookings/${bookingId}/check-in`, { id_proof: idProof }),
  
  // Check-out
  checkOut: (bookingId) => localApi.post(`/bookings/${bookingId}/check-out`),
  
  // Reject/Cancel booking
  rejectBooking: (bookingId, reason, otherReason = null) => 
    localApi.put(`/bookings/${bookingId}/reject`, { rejection_reason: reason, other_rejection_reason: otherReason }),
  
  // Get calendar counts
  getCalendarCounts: (propertyId, startDate, endDate) =>
    localApi.get('/bookings/calendar-counts', {
      params: { property_id: propertyId, start_date: startDate, end_date: endDate }
    }),
  
  // Get analytics
  getAnalytics: (propertyId, startDate, endDate, filterBy = 'booking') =>
    localApi.get('/bookings/analytics/summary', {
      params: { property_id: propertyId, start_date: startDate, end_date: endDate, filter_by: filterBy }
    }),
  
  // Get invoice HTML preview URL.
  // iter88 Phase F — points to the new `/invoice-breakdown` route which
  // returns the itemized breakdown when the booking has a `pricing_breakdown`
  // field, and gracefully falls back to the legacy invoice rendering for
  // historical bookings.
  getInvoiceUrl: (bookingId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/bookings/${bookingId}/invoice-breakdown`;
  },

  // Get invoice PDF download URL (same gating as preview).
  getInvoiceDownloadUrl: (bookingId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/bookings/${bookingId}/invoice-breakdown?download=true`;
  },
  
  // Get WhatsApp share link
  getWhatsappLink: (bookingId) => localApi.get(`/bookings/${bookingId}/whatsapp-link`),

  // Cancel a booking with optional refund (PATCH /bookings/{id}/cancel)
  cancel: (bookingId, refundAmount = 0) =>
    localApi.patch(`/bookings/${bookingId}/cancel`, { refund_amount: Number(refundAmount) || 0 }),

  // Get bookings export (CSV / XLSX) URL — opens directly in browser via window.open
  getExportUrl: ({ format = 'xlsx', startDate, endDate, propertyId, grbId, propertyName, token }) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const params = new URLSearchParams();
    params.set('format', format);
    params.set('start_date', startDate);
    params.set('end_date', endDate);
    if (grbId) params.set('grb_id', grbId);
    if (propertyId) params.set('property_id', propertyId);
    if (propertyName) params.set('property_name', propertyName);
    if (token) params.set('token', token);
    return `${baseUrl}/api/bookings/export?${params.toString()}`;
  },
};

// ============================================================================
// ADMIN BOOKINGS APIs - For Admin Panel integration
// ============================================================================

export const adminBookingsAPI = {
  // Receive booking from Admin Panel
  receive: (bookingData) => localApi.post('/receive-booking', bookingData),
  
  // Get all admin bookings
  getAll: (propertyId = null) => 
    localApi.get('/admin-bookings/', { params: { property_id: propertyId } }),
  
  // Get single booking
  getById: (bookingId) => localApi.get(`/admin-bookings/${bookingId}`),
  
  // Update booking
  update: (bookingId, data) => localApi.put(`/admin-bookings/${bookingId}`, data),
  
  // Delete booking
  delete: (bookingId) => localApi.delete(`/admin-bookings/${bookingId}`),
};

// ============================================================================
// PROPERTY APIs - Hybrid (CRM + Local)
// ============================================================================

export const propertyAPI = {
  // Get properties from CRM (live data)
  getFromCRM: () => crmAPI.getProperties(),
  
  // Get properties from local DB
  getAll: () => localApi.get('/properties'),
  getById: (id) => localApi.get(`/properties/${id}`),
  create: (data) => localApi.post('/properties', data),
  update: (id, data) => localApi.put(`/properties/${id}`, data),
  delete: (id) => localApi.delete(`/properties/${id}`),

  // Room types (Hybrid source for manual booking dropdown)
  getRoomTypes: (id) => localApi.get(`/properties/${id}/room-types`),
  addRoomType: (id, data) => localApi.post(`/properties/${id}/room-types`, data),
  updateRoomType: (id, rtId, data) => localApi.put(`/properties/${id}/room-types/${rtId}`, data),
  deleteRoomType: (id, rtId) => localApi.delete(`/properties/${id}/room-types/${rtId}`),

  // iter87 Phase C — Auto-pricing for the Walk-in booking form
  getPricing: (id, params) =>
    localApi.get(`/properties/${id}/pricing`, { params }),

  // iter91 — Multi-room auto-pricing (replaces single-room call in BookingCreateScreen)
  getPricingMulti: (id, body) =>
    localApi.post(`/properties/${id}/pricing-multi`, body),

  // iter91 — Live room-type availability for the Add-Booking flow
  getAvailability: (id, params) =>
    localApi.get(`/properties/${id}/availability`, { params }),

  // iter88 Phase D — Paid facilities from onboarding
  getFacilities: (id) =>
    localApi.get(`/properties/${id}/facilities`),
};

// iter92 — Booking approval workflow helpers
export const bookingApprovalAPI = {
  approve: (bookingId) =>
    localApi.post(`/bookings/${bookingId}/approve`),
  reject: (bookingId, reason, other_reason) =>
    localApi.post(`/bookings/${bookingId}/reject`, { reason, other_reason: other_reason || null }),
  snehaApprove: (bookingId) =>
    localApi.post(`/admin/bookings/${bookingId}/sneha-approve`),
};

// iter106 — Guest check-in & Aadhaar document workflow
export const guestDocumentAPI = {
  upload: (bookingId, file, documentType = 'AADHAAR') => {
    const fd = new FormData();
    fd.append('booking_id', bookingId);
    fd.append('document_type', documentType);
    fd.append('file', file);
    return localApi.post('/guest-documents/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  getForBooking: (bookingId) => localApi.get(`/guest-documents/booking/${bookingId}`),
  audit: (documentId) => localApi.get(`/guest-documents/${documentId}/audit`),
  checkinConfirm: (bookingId) => localApi.post(`/bookings/${bookingId}/checkin-confirm`),
  noShow: (bookingId) => localApi.post(`/bookings/${bookingId}/no-show`),
};

// iter111 — Guest WhatsApp Conversation (read-only)
export const whatsappConversationAPI = {
  getForBooking: (bookingId) => localApi.get(`/whatsapp-conversation/booking/${bookingId}`),
  bulkStatus: (bookingIds) => localApi.post('/whatsapp-conversation/bulk-status', { booking_ids: bookingIds }),
  // iter131 — full conversation module
  inbox: () => localApi.get('/whatsapp-conversation/inbox'),
  messages: (bookingId) => localApi.get(`/whatsapp-conversation/messages/${bookingId}`),
  markRead: (bookingId) => localApi.post(`/whatsapp-conversation/mark-read/${bookingId}`),
  unreadTotal: () => localApi.get('/whatsapp-conversation/unread-total'),
};

// Onboarding (Hotel Master Form) — single source of truth in app/admin DB
export const onboardingAPI = {
  get: (propertyId) => localApi.get(`/onboarding/${propertyId}`),
  save: (propertyId, data) => localApi.put(`/onboarding/${propertyId}`, data),
  complete: (propertyId) => localApi.post(`/onboarding/${propertyId}/complete`),
  upload: (propertyId, field, file) => {
    const fd = new FormData();
    fd.append('field', field);
    fd.append('file', file);
    return localApi.post(`/onboarding/${propertyId}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ============================================================================
// OTA APIs
// ============================================================================

export const otaAPI = {
  getAll: () => localApi.get('/otas/'),
  getById: (id) => localApi.get(`/otas/${id}`),
  create: (data) => localApi.post('/otas/', data),
  update: (id, data) => localApi.put(`/otas/${id}`, data),
  delete: (id) => localApi.delete(`/otas/${id}`),
};

// ============================================================================
// CRM SYNC APIs - Proxy through local backend
// ============================================================================

export const crmSyncAPI = {
  // Public endpoints (no auth)
  getPublicLeads: () => localApi.get('/crm/public/leads'),
  getPublicProperties: () => localApi.get('/crm/public/properties'),
  getPublicHealth: () => localApi.get('/crm/public/health'),
  
  // Protected endpoints
  getProfile: (propertyId) => localApi.get(`/crm/profile/${propertyId}`),
  syncAll: () => localApi.post('/crm/sync'),
  syncProperty: (propertyId, crmPropertyId = null) => 
    localApi.post(`/crm/sync/${propertyId}`, null, { 
      params: crmPropertyId ? { crm_property_id: crmPropertyId } : {} 
    }),
  listCrmProperties: () => localApi.get('/crm/properties'),
  autoSync: (propertyId) => localApi.post(`/crm/auto-sync/${propertyId}`),
  autoSyncAll: () => localApi.post('/crm/auto-sync'),
  getSyncStatus: (propertyId = null) => 
    localApi.get('/crm/sync-status', { params: propertyId ? { property_id: propertyId } : {} }),
  getLastSynced: (propertyId) => localApi.get(`/crm/last-synced/${propertyId}`),
  backgroundSync: (propertyId) => localApi.post(`/crm/background-sync/${propertyId}`),
};

// ============================================================================
// NOTIFICATION APIs
// ============================================================================

export const notificationAPI = {
  registerDevice: (token) => localApi.post('/notifications/register', { token }),
  unregisterDevice: (token) => localApi.post('/notifications/unregister', { token }),
  getSettings: () => localApi.get('/notifications/settings'),
  updateSettings: (settings) => localApi.put('/notifications/settings', settings),
  // iter137 — Bell dropdown notifications backed by /api/push/notifications.
  list: (params = {}) => localApi.get('/push/notifications', { params }),
  markAllRead: () => localApi.post('/push/notifications/read', {}),
  markOneRead: (notification_id) => localApi.post('/push/notifications/read', { notification_id }),
};

// ============================================================================
// ANNOUNCEMENT APIs
// ============================================================================

export const announcementAPI = {
  getAll: () => localApi.get('/announcements'),
  getById: (id) => localApi.get(`/announcements/${id}`),
  create: (data) => localApi.post('/announcements', data),
};

// ============================================================================
// PROFILE APIs (photo upload)
// ============================================================================

export const profileAPI = {
  getPhoto: () => localApi.get('/profile/photo'),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return localApi.post('/profile/photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: () => localApi.delete('/profile/photo'),
};

// ============================================================================
// DEFAULT EXPORT - For backward compatibility
// ============================================================================

// Alias for backward compatibility (some files use bookingAPI instead of bookingsAPI)
export const bookingAPI = bookingsAPI;

const api = localApi;
export default api;
